import { useCallback } from "react";
import { Box, Switch, Text } from "@salestrak/ui";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";

export type DashboardPageSettings = {
  showGreeting: boolean;
};

const DEFAULT_DASHBOARD_PAGE_SETTINGS: DashboardPageSettings = {
  showGreeting: true,
};

const DASHBOARD_PAGE_SETTINGS_KEY = "my-work-page-settings";

// Module-level normalizer (useUserSetting memoizes on it): defaults fill any
// missing keys so the shape can grow without a migration.
function parsePageSettings(value: unknown): DashboardPageSettings {
  return {
    ...DEFAULT_DASHBOARD_PAGE_SETTINGS,
    ...(typeof value === "object" && value !== null
      ? (value as Partial<DashboardPageSettings>)
      : {}),
  };
}

/** Page chrome settings persist in the user-scoped settings table (the
 *  workspace-NULL `setting` bag), so they follow the user across devices —
 *  never localStorage. */
export function useDashboardPageSettings() {
  const { value, setValue } = useUserSetting<DashboardPageSettings>(
    DASHBOARD_PAGE_SETTINGS_KEY,
    parsePageSettings,
  );
  const update = useCallback(
    (patch: Partial<DashboardPageSettings>) => setValue({ ...value, ...patch }),
    [value, setValue],
  );
  return { state: value, update };
}

/**
 * Personal page chrome settings live outside the shared widget view so viewers
 * can tune their My Work surface without requiring `widgets.manage`. Rendered
 * as a section inside the Manage-widgets panel (the host passes it via the
 * panel's `settings` slot) — My Work has ONE gear, one side panel, for both
 * the widget toggles and the page chrome.
 */
export function DashboardPageSettingsSection({
  settings,
  onUpdate,
}: {
  settings: DashboardPageSettings;
  onUpdate: (patch: Partial<DashboardPageSettings>) => void;
}) {
  return (
    <Box direction="column" gap="sm">
      <Text size="xs" variant="muted" weight="medium">
        Page settings
      </Text>
      <Box direction="row" align="center" justify="between" gap="lg">
        <Text size="sm" weight="medium">
          Page greeting
        </Text>
        <Switch
          checked={settings.showGreeting}
          onCheckedChange={(showGreeting) => onUpdate({ showGreeting })}
        />
      </Box>
    </Box>
  );
}
